/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ImageUrlGenerator } from "@tivocorp/data-domain-lib";
import { ImageType } from "@tivocorp/data-domain-lib/dist/core/device/ImageType";

import { ChannelsInteractor } from "../../model/interactor/channelsInteractor";
import { Log } from "../../model/logging";
import {
  TMyHubCarouselLayout,
  TMyHubProgramType,
  TMyHubType,
  TMyHubBadge
} from "../../model/types";
import type {
  TContentMyHubItem,
  TFolderMyHubItem,
  TMyHubCarousel,
  TMyHubItemUnion,
  TRecordingMyHubItem
} from "../../model/types";
import type { TCarouselItem, TContentType } from "../../model/types";
import { CarouselTemplate, OfferType } from "../../model/enums";
import { getTilePixelDimensions } from "../utility/CarouselTemplateUtil";
import { resolveInternalRatingNames } from "../utility/internalRatings";
import type {
  TTilePage,
  TTilePaginationState
} from "../utility/carouselPagination";
import type { TMyHubCarouselData } from "./myHub.types";

const TAG: string = "myHubMappers";

const imageUrlGenerator: ImageUrlGenerator = new ImageUrlGenerator();

/**
 * Maps a TMyHubCarouselLayout string from the API to the app's CarouselTemplate enum.
 */
const mapLayoutToCarouselTemplate = (
  layout: TMyHubCarouselLayout
): CarouselTemplate => {
  switch (layout) {
    case TMyHubCarouselLayout.LANDSCAPE_HORIZONTAL:
      return CarouselTemplate.LANDSCAPE_HORIZONTAL;
    case TMyHubCarouselLayout.LANDSCAPE_GRID:
      return CarouselTemplate.LANDSCAPE_HORIZONTAL;
    case TMyHubCarouselLayout.PIP_LANDSCAPE_HORIZONTAL:
      return CarouselTemplate.PIP_LANDSCAPE_HORIZONTAL;
    case TMyHubCarouselLayout.PIP_LANDSCAPE_GRID:
      return CarouselTemplate.PIP_LANDSCAPE_HORIZONTAL;
    case TMyHubCarouselLayout.LOGO_HORIZONTAL:
      return CarouselTemplate.LOGO_HORIZONTAL;
    case TMyHubCarouselLayout.POSTER_HORIZONTAL:
    default:
      return CarouselTemplate.POSTER_HORIZONTAL;
  }
};

const hasMyHubBadge = (
  badges: TMyHubBadge[] | undefined,
  badge: TMyHubBadge
): boolean => {
  return badges !== undefined && badges !== null
    ? badges.indexOf(badge) !== -1
    : false;
};

const buildMyHubImageUrl = (
  imageHostBaseUrl: string,
  digItemId: string | undefined,
  imageType: ImageType,
  carouselTemplate?: CarouselTemplate
): string => {
  if (imageHostBaseUrl.length === 0) {
    return "";
  }

  const resolvedTemplate =
    carouselTemplate === undefined
      ? CarouselTemplate.POSTER_HORIZONTAL
      : carouselTemplate;

  const { width, height } = getTilePixelDimensions(resolvedTemplate);

  return imageUrlGenerator.buildImageUrl(
    imageHostBaseUrl,
    digItemId || "",
    imageType,
    width,
    height
  );
};

/**
 * Builds the landscape backdrop image for the Recording Highlight popup's
 * browser layout — the same `LANDSCAPE_HORIZONTAL` art and pixel dimensions
 * every other landscape rail already requests (see
 * `mapFeedItemToCarouselItem` in homescreenMappers.ts, which resolves this
 * same template's own `getTilePixelDimensions` the same way), not a crop
 * or a size of the backdrop's own invention. The CSS box that renders it
 * (`object-fit: cover`) is what scales the art up to the panel's Figma
 * 500×285, exactly as any other tile image is scaled to fit its own box —
 * requesting the pixels at that box's size would only ask the image service
 * to do work the browser already does for free.
 */
const buildMyHubBackdropImageUrl = (
  imageHostBaseUrl: string,
  digItemId: string | undefined,
  imageType: ImageType
): string => {
  if (imageHostBaseUrl.length === 0) {
    return "";
  }

  const { width, height } = getTilePixelDimensions(
    CarouselTemplate.LANDSCAPE_HORIZONTAL
  );

  return imageUrlGenerator.buildImageUrl(
    imageHostBaseUrl,
    digItemId || "",
    imageType,
    width,
    height
  );
};

/**
 * Maps a TMyHubProgramType to a domain-lib FeedItemType-like string for image type resolution.
 */
const mapProgramTypeToFeedItemType = (
  programType: TMyHubProgramType | undefined
): string => {
  switch (programType) {
    case TMyHubProgramType.MOVIE:
      return "Movie";
    case TMyHubProgramType.EVENT:
    case TMyHubProgramType.GAME:
    case TMyHubProgramType.MATCH:
    case TMyHubProgramType.RACE:
      return "SportsEvent";
    case TMyHubProgramType.EPISODE:
    case TMyHubProgramType.SHOW:
    default:
      return "Episode";
  }
};

/**
 * Maps a TMyHubProgramType to the shared TContentType used by the Carousel tile.
 */
const mapProgramTypeToContentType = (
  programType: TMyHubProgramType | undefined
): TContentType => {
  if (programType === TMyHubProgramType.MOVIE) {
    return "movie";
  }
  if (
    programType === TMyHubProgramType.EVENT ||
    programType === TMyHubProgramType.GAME ||
    programType === TMyHubProgramType.MATCH ||
    programType === TMyHubProgramType.RACE
  ) {
    return "sports";
  }
  if (
    programType === TMyHubProgramType.EPISODE ||
    programType === TMyHubProgramType.SHOW
  ) {
    return "episode";
  }
  return "episode";
};

/**
 * The rating every My Hub surface shows ("PG-13", "TV-G") — the tile badge,
 * the list cards' metadata and the Recording Highlight's info cards all read
 * the tile's `ratingBadge`, which is set here and nowhere else.
 *
 * The service's `internalRatingList` is resolved through the internal ratings
 * table exactly as previews resolve theirs (see
 * `PreviewFormatter.getInternalRatings`), and the first name it yields is
 * shown. The plain `rating` string is the fallback for an item whose list is
 * absent or resolves to nothing — e.g. before the ratings table has loaded.
 *
 * @param item - The My Hub item off the wire.
 * @returns The rating to show, or `undefined` when there is none.
 */
const resolveMyHubRating = (item: TContentMyHubItem): string | undefined => {
  const internalRatingNames: string[] = resolveInternalRatingNames(
    item.internalRatingList
  );
  if (internalRatingNames.length > 0) {
    return internalRatingNames[0];
  }
  return item.rating !== undefined && item.rating !== null && item.rating !== ""
    ? item.rating
    : undefined;
};

/**
 * Maps a TContentMyHubItem (non-recording) to a TCarouselItem.
 * Constructs the image URL using imageHostBaseUrl and tile dimensions,
 * following the homescreen mapper pattern.
 */
const mapContentMyHubItem = (
  item: TContentMyHubItem,
  imageHostBaseUrl: string,
  carouselTemplate?: CarouselTemplate
): TCarouselItem => {
  const contentType: TContentType = mapProgramTypeToContentType(
    item.programType
  );

  const feedItemType: string = mapProgramTypeToFeedItemType(item.programType);

  const imageType = imageUrlGenerator.getImageTypeFromFeedItemType(
    feedItemType as Parameters<
      typeof imageUrlGenerator.getImageTypeFromFeedItemType
    >[0]
  );

  const imageUrl = buildMyHubImageUrl(
    imageHostBaseUrl,
    item.digItemId,
    imageType,
    carouselTemplate
  );
  const backdropImageUrl = buildMyHubBackdropImageUrl(
    imageHostBaseUrl,
    item.digItemId,
    imageType
  );

  return {
    id: item.myHubItemId,
    // The service's own discriminator, carried through so a view branches on
    // it rather than inferring the item shape from the mapped fields. The
    // recording and folder mappers overwrite it with their own type.
    myHubItemType: TMyHubType.CONTENT,
    title: item.title,
    description:
      item.episodeTitle !== undefined && item.episodeTitle !== null
        ? item.episodeTitle
        : "",
    imageUrl,
    backdropImageUrl,
    contentType,
    categoryLabel: "",
    duration:
      item.duration !== undefined && item.duration !== null ? item.duration : 0,
    isAdult:
      item.isAdult !== undefined && item.isAdult !== null
        ? item.isAdult
        : false,
    progressPercent: item.percentageWatched,
    movieYear:
      item.movieYear !== undefined && item.movieYear !== null
        ? Number(item.movieYear)
        : undefined,
    seasonNumber: item.seasonOrYear,
    episodeNumber: item.episodeNumber,
    episodeTitle: item.episodeTitle,
    ratingBadge: resolveMyHubRating(item),
    // Tile Badges to be rendered on the tile, derived from the MyHub badges array.
    isNew: hasMyHubBadge(item.badges, TMyHubBadge.NEW),
    isSaved: hasMyHubBadge(item.badges, TMyHubBadge.SAVED),
    isWatched: hasMyHubBadge(item.badges, TMyHubBadge.WATCHED),
    isRecording: hasMyHubBadge(item.badges, TMyHubBadge.RECORDING),
    contentId: item.contentId,
    availableActions:
      item.availableActions !== undefined ? [...item.availableActions] : []
  };
};

/**
 * Maps a TRecordingMyHubItem to a TCarouselItem.
 * Scheduled items (no recordingId, offerId present) are treated as upcoming.
 */
const mapRecordingMyHubItem = (
  item: TRecordingMyHubItem,
  imageHostBaseUrl: string,
  carouselTemplate?: CarouselTemplate
): TCarouselItem => {
  const isScheduled: boolean =
    item.recordingId === undefined || item.recordingId === null;

  const base: TCarouselItem = mapContentMyHubItem(
    item as unknown as TContentMyHubItem,
    imageHostBaseUrl,
    carouselTemplate
  );

  return {
    ...base,
    myHubItemType: TMyHubType.RECORDING,
    offerType: isScheduled ? OfferType.Live : OfferType.Recording,
    // Carried through rather than left to the caller to look up: it is the id
    // the recordings service takes, and `id` above is the myHubItemId, not it.
    recordingId: item.recordingId,
    startTime: item.startTime,
    endTime: item.endTime,
    stationId: item.stationId,
    expiresOn: item.expiresOn
  };
};

/**
 * Maps a TFolderMyHubItem to a TCarouselItem.
 * Folders represent TV series or movie collections.
 * Constructs the image URL using imageHostBaseUrl and tile dimensions.
 */
const mapFolderMyHubItem = (
  item: TFolderMyHubItem,
  imageHostBaseUrl: string,
  carouselTemplate?: CarouselTemplate
): TCarouselItem => {
  const imageUrl = buildMyHubImageUrl(
    imageHostBaseUrl,
    item.digItemId,
    "COLLECTION",
    carouselTemplate
  );

  return {
    id: item.myHubItemId,
    myHubItemType: TMyHubType.FOLDER,
    title: item.title,
    description: "",
    imageUrl,
    contentType: "series",
    categoryLabel: "",
    duration: 0,
    isAdult:
      item.isAdult !== undefined && item.isAdult !== null
        ? item.isAdult
        : false,
    recordingCount: item.recordingCount,
    // The id the manage-series screen needs — see TCarouselItem.unifiedCollectionId.
    unifiedCollectionId: item.unifiedCollectionId,
    // Tile Badges to be rendered on the tile, derived from the MyHub badges array.
    isNew: hasMyHubBadge(item.badges, TMyHubBadge.NEW),
    isSaved: hasMyHubBadge(item.badges, TMyHubBadge.SAVED),
    isWatched: hasMyHubBadge(item.badges, TMyHubBadge.WATCHED),
    isRecording: hasMyHubBadge(item.badges, TMyHubBadge.RECORDING),
    availableActions:
      item.availableActions !== undefined ? [...item.availableActions] : []
  };
};

/**
 * Maps a single TMyHubItemUnion to a TCarouselItem for use by the shared Carousel.
 */
export const mapMyHubItemToCarouselItem = (
  item: TMyHubItemUnion,
  imageHostBaseUrl: string = "",
  carouselTemplate?: CarouselTemplate
): TCarouselItem => {
  if (item.myHubItemType === TMyHubType.RECORDING) {
    return mapRecordingMyHubItem(
      item as TRecordingMyHubItem,
      imageHostBaseUrl,
      carouselTemplate
    );
  }

  if (item.myHubItemType === TMyHubType.FOLDER) {
    return mapFolderMyHubItem(
      item as TFolderMyHubItem,
      imageHostBaseUrl,
      carouselTemplate
    );
  }

  return mapContentMyHubItem(
    item as TContentMyHubItem,
    imageHostBaseUrl,
    carouselTemplate
  );
};

/**
 * Maps a TMyHubCarousel to the MyHubCarouselData view shape,
 * converting each TMyHubItemUnion tile to TCarouselItem.
 *
 * The return type is intentionally structural — callers in MyHubScreen cast
 * the result to MyHubCarouselData which is a local view type.
 */
export const mapMyHubCarousel = (
  carousel: TMyHubCarousel,
  imageHostBaseUrl: string = ""
): {
  carouselId: string;
  title: string | undefined;
  layout: CarouselTemplate;
  resolvedCount: number | undefined;
  items: TCarouselItem[];
} => {
  const resolvedLayout = mapLayoutToCarouselTemplate(carousel.layout);
  return {
    carouselId: carousel.carouselId,
    title: carousel.title,
    layout: resolvedLayout,
    resolvedCount: carousel.resolvedCount,
    items: carousel.items.map(
      (item: TMyHubItemUnion): TCarouselItem =>
        mapMyHubItemToCarouselItem(item, imageHostBaseUrl, resolvedLayout)
    )
  };
};

/**
 * Maps a single-carousel response (GET /v1/myHub/carousels/{carouselId}) to an
 * offset-paginated page of tiles, for {@link createLoadMoreTilesThunk}'s
 * `fetchPage` — mirrors `mapCarouselResponseToTilePage` in
 * `homescreenMappers.ts`. `resolvedCount` falls back to `items.length` when
 * the row response omits it.
 */
export const mapMyHubCarouselToTilePage = (
  carousel: TMyHubCarousel,
  imageHostBaseUrl: string = ""
): TTilePage => {
  const resolvedLayout = mapLayoutToCarouselTemplate(carousel.layout);
  const items: TCarouselItem[] = carousel.items.map(
    (item: TMyHubItemUnion): TCarouselItem =>
      mapMyHubItemToCarouselItem(item, imageHostBaseUrl, resolvedLayout)
  );
  return {
    items,
    resolvedCount:
      carousel.resolvedCount !== undefined
        ? carousel.resolvedCount
        : items.length
  };
};

/**
 * Seeds the per-row offset tile pagination map from the already-mapped
 * carousels of an initial GET /v1/myHub/carousels response — mirrors
 * `mapCarouselsResponseToInitialTilePagination` in `homescreenMappers.ts`.
 * Each row starts with the items already returned in that response.
 */
export const mapMyHubCarouselsToInitialTilePagination = (
  carousels: readonly TMyHubCarouselData[]
): Record<string, TTilePaginationState> => {
  const seed: Record<string, TTilePaginationState> = {};
  for (const carousel of carousels) {
    seed[carousel.carouselId] = {
      items: carousel.items,
      resolvedCount:
        carousel.resolvedCount !== undefined
          ? carousel.resolvedCount
          : carousel.items.length,
      isLoading: false,
      error: ""
    };
  }
  return seed;
};

// ── Channel names ────────────────────────────────────────────────────────────

/**
 * Channel-name lookups for My Hub's airing lines ("Today  9:00-10:00pm on NBC").
 *
 * The wire model carries only `stationId`; the name lives in the channel list,
 * which `ChannelsInteractor` reads from its Storage cache. A miss is never an
 * error — an unsubscribed or stale station simply has no name to print, and
 * `composeSeriesAiringLabel` drops the clause rather than showing an id.
 *
 * Here rather than beside one caller because every My Hub surface that prints
 * the clause resolves it the same way: the recording highlight (one station, on
 * a press) and the manage-series and scheduled-series lists (one per row, on a
 * fetch) all come through here, so the fallback rules have one home.
 */

/**
 * Name for one station, preferring the channel's display name over its call
 * sign, or `""` when it cannot be resolved.
 *
 * @param stationId - Station id off the wire, possibly absent.
 * @returns The channel name, or `""`.
 */
export const resolveChannelName = async (
  stationId: string | undefined
): Promise<string> => {
  if (stationId === undefined || stationId === "") {
    return "";
  }

  try {
    const interactor: ChannelsInteractor = new ChannelsInteractor();
    const channel = await interactor.getChannelByStationId(stationId);
    if (channel === null) {
      Log.w(
        TAG,
        `resolveChannelName() — no channel for stationId=${stationId}`
      );
      return "";
    }
    if (channel.name !== undefined && channel.name !== "") {
      return channel.name;
    }
    return channel.callSign !== undefined ? channel.callSign : "";
  } catch (error: unknown) {
    // The caller's screen is worth showing without the channel clause — a cache
    // read failing is not a reason to fail the fetch or swallow a press.
    Log.w(TAG, `resolveChannelName() — lookup failed: ${String(error)}`);
    return "";
  }
};

/**
 * Names for a set of stations, as a `stationId → name` map.
 *
 * Deduplicates first: a series folder is usually one channel repeated down the
 * list, so a folder of twelve episodes is one lookup rather than twelve. The
 * lookups run together and each falls back independently, so one unresolvable
 * station costs its own clause and nothing else.
 *
 * @param stationIds - Station ids, in any order, with duplicates and blanks.
 * @returns Map of station id to channel name; stations that resolved to nothing
 * are absent, which reads the same as `""` through {@link toCallSignResolver}.
 */
export const resolveChannelNames = async (
  stationIds: readonly (string | undefined)[]
): Promise<Record<string, string>> => {
  const unique: string[] = [];
  stationIds.forEach((stationId: string | undefined): void => {
    if (
      stationId !== undefined &&
      stationId !== "" &&
      unique.indexOf(stationId) === -1
    ) {
      unique.push(stationId);
    }
  });

  const names: string[] = await Promise.all(
    unique.map(
      (stationId: string): Promise<string> => resolveChannelName(stationId)
    )
  );

  const resolved: Record<string, string> = {};
  unique.forEach((stationId: string, index: number): void => {
    const name: string = names[index];
    if (name !== "") {
      resolved[stationId] = name;
    }
  });
  return resolved;
};

/**
 * Turns a resolved map into the synchronous lookup the mappers take.
 *
 * The mappers are pure and cannot await, which is why the resolution happens in
 * the thunk and arrives here as a closure over what it found.
 *
 * @param resolved - Map from {@link resolveChannelNames}.
 * @returns A lookup that answers `""` for anything absent.
 */
export const toCallSignResolver = (
  resolved: Record<string, string>
): ((stationId: string | undefined) => string) => {
  return (stationId: string | undefined): string => {
    if (stationId === undefined || stationId === "") {
      return "";
    }
    const name: string | undefined = resolved[stationId];
    return name === undefined ? "" : name;
  };
};
